/**
 * Protects in-progress page state from a forced re-login redirect.
 *
 * KeycloakService.doLogin() navigates the whole tab away and back, which wipes
 * all React state. That's fine for a fresh/explicit login (nothing to lose),
 * but not when it's forced mid-use by a dead session -- whatever the user was
 * typing/building on the current page needs to survive the round trip.
 *
 * Usage: a page/component calls useDraftRecovery(key, value, onRestore) (see
 * hooks/useDraftRecovery.ts) to register its current state. Right before a
 * forced re-login redirect, captureRecoverableState() snapshots every
 * currently-mounted registrant to localStorage; each one picks its snapshot
 * back up the next time it mounts, once, via the hook's loadDraft() call.
 *
 * Each snapshot is stamped with the `sub` of the user who saved it and is
 * only ever handed back to that same user, so a forced re-login completed by
 * someone else on a shared browser can't restore the previous user's work
 * into their session.
 */

const STORAGE_PREFIX = 'bite.sessionRecovery.';
// Long enough to cover a redirect-to-Keycloak-and-back round trip (including a
// user who has to actually re-enter credentials), short enough that a draft
// never resurfaces confusingly on some unrelated visit days later.
const MAX_DRAFT_AGE_MS = 15 * 60 * 1000;

type RecoverableGetter = () => unknown;

const recoverables = new Map<string, RecoverableGetter>();

/**
 * Registers `getSnapshot` as the current-state source for `key`. Returns an
 * unregister function -- call it on unmount so a stale getter from a
 * no-longer-mounted component can't be captured.
 */
export const registerRecoverable = (
  key: string,
  getSnapshot: RecoverableGetter,
): (() => void) => {
  recoverables.set(key, getSnapshot);
  return () => {
    if (recoverables.get(key) === getSnapshot) {
      recoverables.delete(key);
    }
  };
};

// KeycloakService imports withRecoveryDraftsPreserved from THIS module, so
// importing it back just to read the token would be a require cycle. Read the
// stored access token directly instead; keep this in sync with
// KC_ACCESS_TOKEN_KEY in services/KeycloakService.js.
const KC_ACCESS_TOKEN_KEY = 'bite.kc.accessToken';

/**
 * `sub` of whoever is authenticated in this tab right now, or null when that
 * can't be determined -- no token stored yet (the earliest render after a
 * redirect, before the code-for-token exchange finishes) or an unparseable
 * one. Callers must treat null as "unknown", never as "nobody".
 */
export const currentOwner = (): string | null => {
  try {
    const token =
      sessionStorage.getItem(KC_ACCESS_TOKEN_KEY) ||
      localStorage.getItem(KC_ACCESS_TOKEN_KEY);
    if (!token) return null;
    const claims = JSON.parse(atob(token.split('.')[1]));
    return typeof claims?.sub === 'string' ? claims.sub : null;
  } catch (error) {
    return null;
  }
};

const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`;

export const saveDraft = (key: string, data: unknown): void => {
  try {
    localStorage.setItem(
      storageKey(key),
      JSON.stringify({ savedAt: Date.now(), owner: currentOwner(), data: data }),
    );
  } catch (error) {
    // Private browsing / storage quota -- losing the draft beats crashing.
    console.warn('sessionRecovery: failed to save draft', key, error);
  }
};

/** Reads and clears the draft for `key`. Returns null if absent or stale. */
export const loadDraft = <T = unknown>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    localStorage.removeItem(storageKey(key));

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS) return null;

    // A forced re-login can be finished by a DIFFERENT person on a shared
    // browser, and these drafts carry product / export / beneficial-ownership
    // work. Hand one back only if the session that came back belongs to
    // whoever saved it. The entry was already removed above, so a mismatch
    // drops it instead of leaving it for the next login to pick up.
    //
    // Both sides have to be known to compare: `owner` is null for drafts
    // written before this check existed, and currentOwner() is null before the
    // returning session's token is stored -- in either case this falls through
    // to the previous behaviour rather than silently dropping a valid draft.
    const owner = typeof parsed.owner === 'string' ? parsed.owner : null;
    const viewer = currentOwner();
    if (owner && viewer && owner !== viewer) return null;

    return parsed.data as T;
  } catch (error) {
    console.warn('sessionRecovery: failed to load draft', key, error);
    return null;
  }
};

/**
 * Snapshots every currently-mounted recoverable to localStorage. Call this
 * right before KeycloakService.doLogin() when the redirect is forced by a
 * dead session mid-use -- never before an explicit/fresh login, where there's
 * nothing worth restoring and doing so could resurrect a previous user's
 * draft on a shared machine.
 */
export const captureRecoverableState = (): void => {
  recoverables.forEach((getSnapshot, key) => {
    try {
      saveDraft(key, getSnapshot());
    } catch (error) {
      console.warn('sessionRecovery: failed to capture', key, error);
    }
  });
};

/**
 * Runs `clearFn` (expected to wipe localStorage, e.g. KeycloakService's
 * doLogout()) while preserving any pending session-recovery drafts across it.
 *
 * A dead session detected by the axios interceptor forces doLogout(), not
 * doLogin() -- and doLogout() unconditionally clears localStorage as part of
 * ending the session. Without this, a draft captureRecoverableState() just
 * wrote would be destroyed a few lines later by that same clear, in the exact
 * scenario (a workflow tool's own save/load call hitting a dead session) this
 * whole mechanism exists to protect. Only ever called from the forced/dead-
 * session path -- an explicit, user-initiated sign-out clears everything, no
 * exception, since a leftover draft has no business surviving onto whoever
 * uses the browser next.
 */
export const withRecoveryDraftsPreserved = (clearFn: () => void): void => {
  const preserved: Array<[string, string]> = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value !== null) preserved.push([key, value]);
      }
    }
  } catch (error) {
    console.warn('sessionRecovery: failed to snapshot drafts before clear', error);
  }

  clearFn();

  try {
    preserved.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch (error) {
    console.warn('sessionRecovery: failed to restore drafts after clear', error);
  }
};

/**
 * Whether a forced, already-state-preserved navigation is about to happen --
 * set right before KeycloakService.doLogout()'s dead-session redirect
 * (_kc.logout(), which navigates the whole tab) when it was called with
 * preserveRecoveryDrafts: true.
 *
 * That flag is only ever passed on the forced/dead-session path (see
 * axiosHelper.ts's interceptor), never on an explicit user-initiated logout
 * click -- and this mirrors that same distinction for a DIFFERENT problem: a
 * page's own "unsaved changes" `beforeunload` guard has no way to tell a
 * forced, already-safe redirect apart from the user actually trying to
 * navigate away, so it prompts for both. On the forced path that's not just
 * confusing but wrong -- captureRecoverableState() already ran, so nothing
 * is actually at risk of being lost -- and if the user had already dismissed
 * one real "Leave site?" prompt moments earlier (session died mid-session,
 * the very next request triggers this), a second one for a redirect they
 * never asked for is exactly the doubled-dialog bug reported against the
 * Tariff Checker.
 *
 * A page's beforeunload handler should check isForcedNavigationInFlight()
 * and skip preventDefault() when true, instead of warning on a navigation
 * that isn't the user's choice and can't lose their work.
 */
let forcedNavigationInFlight = false;

export const markForcedNavigation = (): void => {
  forcedNavigationInFlight = true;
};

export const isForcedNavigationInFlight = (): boolean => forcedNavigationInFlight;
